import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Input, List, Modal, Space, Switch, Tooltip, Typography, message } from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TH5PLessonConfig, TH5PQuiz } from '../../../models/courses/h5p';
import JsonEditor from '../../JsonEditor/JsonEditor';
import { createDefaultQuiz, sanitizeQuiz } from '../utils';
import H5PInteractionEditor from './H5PInteractionEditor';

interface H5PLessonAdminProps {
  config: TH5PLessonConfig;
  onConfigChange: (config: TH5PLessonConfig) => void;
  onSave: () => Promise<void> | void;
  saving?: boolean;
  initialStatement?: string;
  onStatementChange?: (statement: string) => void;
}

const ensureConfigIntegrity = (config: TH5PLessonConfig): TH5PLessonConfig => ({
  videoUrl: config.videoUrl ?? '',
  allowSkipping: config.allowSkipping ?? false,
  quizzes: (config.quizzes ?? []).map(sanitizeQuiz).sort((a, b) => a.time - b.time),
});

const H5PLessonAdmin: React.FC<H5PLessonAdminProps> = ({
  config,
  onConfigChange,
  onSave,
  saving = false,
  initialStatement,
  onStatementChange,
}) => {
  const { t } = useTranslation();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState<TH5PQuiz | undefined>(undefined);
  const [importOpen, setImportOpen] = useState(false);

  const normalizedConfig = useMemo(() => ensureConfigIntegrity(config), [config]);

  const handleVideoUrlChange = (value: string) => {
    onConfigChange({
      ...normalizedConfig,
      videoUrl: value,
    });
  };

  const handleAllowSkippingChange = (allowSkipping: boolean) => {
    onConfigChange({
      ...normalizedConfig,
      allowSkipping,
    });
  };

  const openEditor = (quiz?: TH5PQuiz) => {
    setEditingQuiz(quiz ? sanitizeQuiz(quiz) : createDefaultQuiz());
    setEditorOpen(true);
  };

  const handleEditorSubmit = (quiz: TH5PQuiz) => {
    const updated = normalizedConfig.quizzes.filter((item) => item.id !== quiz.id);
    const nextConfig: TH5PLessonConfig = {
      ...normalizedConfig,
      quizzes: [...updated, quiz].sort((a, b) => a.time - b.time),
    };
    onConfigChange(nextConfig);
    setEditorOpen(false);
    message.success(t('h5p.admin.messages.quizSaved'));
  };

  const handleRemoveQuiz = (quiz: TH5PQuiz) => {
    const nextConfig: TH5PLessonConfig = {
      ...normalizedConfig,
      quizzes: normalizedConfig.quizzes.filter((item) => item.id !== quiz.id),
    };
    onConfigChange(nextConfig);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button onClick={() => setImportOpen(true)}>{t('h5p.admin.actions.importButton')}</Button>
      </Space>
      <Card title={t('h5p.admin.videoCard.title')}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Typography.Text>{t('h5p.admin.videoCard.description')}</Typography.Text>
          <Input
            value={normalizedConfig.videoUrl}
            placeholder={t('h5p.admin.videoCard.placeholder')}
            onChange={(event) => handleVideoUrlChange(event.target.value)}
            allowClear
            required
          />
          <Space align="center">
            <Switch
              checked={normalizedConfig.allowSkipping}
              onChange={handleAllowSkippingChange}
            />
            <Typography.Text>{t('h5p.admin.videoCard.allowSkipping')}</Typography.Text>
          </Space>
        </Space>
      </Card>

      <Card
        title={t('h5p.admin.quizCard.title')}
        extra={
          <Button icon={<PlusOutlined />} type="primary" onClick={() => openEditor()}>
            {t('h5p.admin.quizCard.add')}
          </Button>
        }
      >
        {normalizedConfig.quizzes.length === 0 ? (
          <Empty description={t('h5p.admin.quizCard.empty')} />
        ) : (
          <List
            dataSource={normalizedConfig.quizzes}
            renderItem={(quizItem) => (
              <List.Item
                actions={[
                  <Tooltip title={t('h5p.admin.quizCard.actions.edit')} key="edit">
                    <Button
                      icon={<EditOutlined />}
                      type="text"
                      onClick={() => openEditor(quizItem)}
                    />
                  </Tooltip>,
                  <Tooltip title={t('h5p.admin.quizCard.actions.delete')} key="delete">
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      type="text"
                      onClick={() => handleRemoveQuiz(quizItem)}
                    />
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space direction="vertical">
                      <Typography.Text strong>
                        {quizItem.title || t('h5p.admin.quizCard.defaultTitle', { time: quizItem.time })}
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        {quizItem.question}
                      </Typography.Text>
                    </Space>
                  }
                  description={
                    <Typography.Text type="secondary">
                      {quizItem.description || t('h5p.admin.quizCard.noDescription')}
                    </Typography.Text>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={() => onSave()}
        >
          {t('h5p.admin.actions.saveConfiguration')}
        </Button>
      </Space>

      <H5PInteractionEditor
        open={editorOpen}
        initialValue={editingQuiz}
        onCancel={() => setEditorOpen(false)}
        onSubmit={handleEditorSubmit}
      />
      <Modal
        title={t('h5p.admin.importDialog.title')}
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <JsonEditor<TH5PLessonConfig>
          title={t('h5p.admin.importDialog.jsonTitle')}
          value={normalizedConfig}
          showStatement={true}
          initialStatement={initialStatement}
          onStatementChange={onStatementChange}
          onApply={(data) => {
            const nextConfig = ensureConfigIntegrity(data);
            onConfigChange(nextConfig);
            setImportOpen(false);
            message.success(t('h5p.admin.importDialog.success'));
          }}
        />
      </Modal>
    </Space>
  );
};

export default H5PLessonAdmin;
